import { Theme, Themed } from "@client/styles/themes";
import { useGSAP } from "@gsap/react";
import gsap from "gsap/dist/gsap";
import React, { useEffect, useRef, useState } from "react";

import { FullBleedMediaBlock as Value } from "@reactivated";

import { HeroLayoutProps } from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";

import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

interface Props {
    media: React.JSX.Element;
    mobileMedia?: React.JSX.Element;
    content: React.JSX.Element | null;
    isInCarousel?: boolean;
    height: Value["height"] | "heightFullMinusNav";
    curtains?: Value["curtains"];
    theme?: Theme;
}

export const FullBleedMedia = ({
    content,
    isInCarousel = false,
    media,
    mobileMedia,
    height,
    curtains,
    theme = "navy",
}: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        if (!curtains) return;

        gsap.to(containerRef.current, {
            "--curtains": 0,
            "scrollTrigger": {
                trigger: containerRef.current,
                scrub: 0.3,
                start: "top 50%",
                end: "bottom bottom",
                fastScrollEnd: true,
            },
        });
    });

    const sharedStyles = {
        mediaClassName: content ? styles.overlay : undefined,
        contentClassName: isInCarousel
            ? styles.contentInCarousel
            : styles.content,
    };

    const [minHeightFull, setMinHeightFull] = useState("100dvh");

    /**
     * A little overengineered; minHeightFull could be coded to `100dvh` and
     * it'll work fine for most. But if old browsers don't support it, this is
     * the SSR-safe way to offer a fallback.
     */
    useEffect(() => {
        if (!CSS.supports("height", "100dvh")) {
            setMinHeightFull("100vh");
        }
    }, []);

    const getHeightAwareProps = (mobile: boolean, hasContent: boolean) => {
        // Hardcoded values are design requirements
        const ASPECT_RATIO = "16:9";
        const FIXED_HEIGHT_DESKTOP = 640;
        const FIXED_HEIGHT_MOBILE = 400;

        let mediaAspectRatio: HeroLayoutProps["settings"]["mediaAspectRatio"] =
            "stretch";

        if (height === "heightAspectRatio") {
            mediaAspectRatio = ASPECT_RATIO;
            // Edge case where content is likely to break the layout
            if (mobile && hasContent) mediaAspectRatio = "none";
        }

        let minHeight: number | string | undefined = undefined;

        switch (height) {
            case "heightFixed":
                minHeight = mobile ? FIXED_HEIGHT_MOBILE : FIXED_HEIGHT_DESKTOP;
                break;
            case "heightFull":
                minHeight = minHeightFull;
                break;
            case "heightFullMinusNav":
                minHeight = `calc(${minHeightFull} - var(--header-height))`;
                break;
        }

        return {
            mediaAspectRatio,
            minHeight,
        };
    };

    const desktopSettings: HeroLayoutProps = {
        content,
        media,
        settings: {
            heroDirection: "row",
            heroPlacement: "backgroundFull",
            mediaPlacement: "background",
            contentAlignPrimary: "right/bottom",
            contentAlignSecondary: "middle",
            textAlignment: "center",
            contentWidth: 8,
            ...getHeightAwareProps(false, !!content),
        },
        styles: sharedStyles,
    };

    const laptopSettings: HeroLayoutProps = {
        ...desktopSettings,
        settings: {
            ...desktopSettings.settings,
            contentWidth: 10,
        },
    };

    const tabletSettings: HeroLayoutProps = {
        ...desktopSettings,
        settings: {
            ...desktopSettings.settings,
            contentWidth: 12,
        },
    };

    const mobileSettings: HeroLayoutProps = {
        ...desktopSettings,
        media: mobileMedia ?? media,
        settings: {
            ...desktopSettings.settings,
            contentAlignPrimary: "right/bottom",
            contentWidth: 12,
            ...getHeightAwareProps(true, !!content),
        },
    };

    return (
        <Themed
            className={curtains ? styles.curtains : undefined}
            ref={containerRef}
            theme={theme}
        >
            <HeroSet
                layouts={{
                    desktop: desktopSettings,
                    laptop: laptopSettings,
                    tablet: tabletSettings,
                    mobile: mobileSettings,
                }}
            />
        </Themed>
    );
};

export const FullBleedMediaBlock = ({ value }: { value: Value }) => {
    if (!value.media || value.media.length === 0) return <></>;

    const content = value.content ? (
        <RichText
            value={value.content}
            textAlignment="center"
            constrainParagraphWidth="half"
            headingSize="large"
        />
    ) : null;

    const media = <MediaChooserBlock value={value.media} />;
    const mobileMedia =
        value.mobile_media && value.mobile_media.length > 0 ? (
            <MediaChooserBlock value={value.mobile_media} />
        ) : undefined;

    return (
        <FullBleedMedia
            content={content}
            media={media}
            mobileMedia={mobileMedia}
            height={value.height}
            curtains={value.curtains}
        />
    );
};
